/**
 * 분기 지도 LLM 호출 — `POST /api/onto/proposal/map`(두 모델 교차 출력: 물품 읽기·후보·가르는 사실) · `POST /api/onto/proposal/ask`(질문).
 * 설계 §3. 백엔드가 없거나 실패하면 `{available:false}`로 돌아와 지도는 shadow만으로 그려진다(fail-open).
 * 응답은 여기서 한 번 더 걸러 프런트가 믿는 모양으로만 넘긴다: 코드는 숫자, 사실 키·값은 문자열.
 */
import { digitsOf } from "./candidateCompareModel";

export const MAP_PATH = "/api/onto/proposal/map";
export const ASK_PATH = "/api/onto/proposal/ask";
export const USAGE_PATH = "/api/onto/proposal/usage";
/** 대화 열에서 고를 수 있는 모델. 표시 이름은 Claude Code 하단 상태 줄처럼 명시한다. */
// 화면에는 역할 이름만 보인다(사용자 결정 2026-09-14: 모델명 노출 부담 → "기본 · 빠름 / 정밀 · 느림").
// 회사명·정확한 모델 문자열은 툴팁(vendor)·로그·사용량 페이지 툴팁에만.
export const ASK_MODELS = [
  { key: "gemini", label: "기본", short: "기본", vendor: "Gemini", hint: "빠르고 저렴 · 대부분의 질문" },
  { key: "opus", label: "정밀", short: "정밀", vendor: "Claude", hint: "더 깊게 · 느림" },
];
export const ASK_MODEL_KEY = "loginhs:ask-model";
export function readAskModel() { try { const v = localStorage.getItem(ASK_MODEL_KEY); return ASK_MODELS.some((m) => m.key === v) ? v : "gemini"; } catch (_) { return "gemini"; } }
export function writeAskModel(key) { try { localStorage.setItem(ASK_MODEL_KEY, key); } catch (_) { /* ignore */ } }
export function askModelLabel(key) { return (ASK_MODELS.find((m) => m.key === key) || ASK_MODELS[0]).short; }
export function askModelVendor(key) { return (ASK_MODELS.find((m) => m.key === key) || ASK_MODELS[0]).vendor; }

function authHeaders() {
  let token = null;
  try { token = localStorage.getItem("token") || sessionStorage.getItem("token"); } catch (_) { token = null; }
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

const MODEL_NAMES = ["gemini", "opus"];
const modelsOf = (arr) => (Array.isArray(arr) ? arr : []).map(String).filter((m) => MODEL_NAMES.includes(m));

/**
 * `POST /api/onto/proposal/map` 응답을 프런트가 믿는 모양으로. 서버가 이미 활성 프레임·질문 키로 검증했지만
 * 여기서 한 번 더 걸러 코드는 숫자, 사실 키·값은 문자열만 남긴다. 두 모델(gemini·opus)의 상태·일치를 그대로 싣는다.
 */
export function normalizeMap(json) {
  if (!json || typeof json !== "object" || !json.map || typeof json.map !== "object") return null;
  const m = json.map;
  const codes = (arr) => (Array.isArray(arr) ? arr : []).map((c) => digitsOf(typeof c === "object" ? c.code : c)).filter((d) => [4, 6, 10].includes(d.length));
  const models = {};
  MODEL_NAMES.forEach((name) => {
    const x = json.models && json.models[name];
    if (!x || typeof x !== "object") return;
    models[name] = { model: String(x.model || name), status: String(x.status || "unavailable"), reason: x.reason ? String(x.reason) : null, latencyMs: Number(x.latency_ms) || 0,
      inTokens: Number(x.in_tokens) || 0, outTokens: Number(x.out_tokens) || 0, summary: typeof x.summary === "string" ? x.summary : "", top: digitsOf(x.top) || null, candidates: codes(x.candidates) };
  });
  const a = json.agreement && typeof json.agreement === "object" ? json.agreement : {};
  return {
    summary: typeof m.product_summary === "string" ? m.product_summary : "",
    summaryModel: MODEL_NAMES.includes(m.summary_model) ? m.summary_model : null,
    notThis: (Array.isArray(m.not_this) ? m.not_this : []).map((x) => ({ code: digitsOf(x.code), why: String(x.why || ""), models: modelsOf(x.models) })).filter((x) => x.code),
    branches: (Array.isArray(m.branches) ? m.branches : []).map((b) => ({
      label: String(b.label || ""), key: typeof b.fact_key === "string" ? b.fact_key : null, mapped: Boolean(b.mapped && b.fact_key), models: modelsOf(b.models),
      options: (Array.isArray(b.options) ? b.options : []).map((o) => ({ label: String(o.label || ""), value: typeof o.value === "string" ? o.value : null, leadsTo: codes(o.leads_to), models: modelsOf(o.models) })).filter((o) => o.label),
    })).filter((b) => b.label && b.options.length),
    candidates: (Array.isArray(m.candidates) ? m.candidates : []).map((c) => ({ code: digitsOf(c.code), level: c.level || null, name: c.name || null, condition: String(c.condition || ""), basis: String(c.basis || ""), models: modelsOf(c.models) })).filter((c) => [4, 6, 10].includes(c.code.length)),
    checks: (Array.isArray(m.checks) ? m.checks : []).map(String),
    mostCommon: Object.fromEntries(MODEL_NAMES.filter((n) => m.most_common && m.most_common[n] && m.most_common[n].code).map((n) => [n, { code: digitsOf(m.most_common[n].code), caveat: String(m.most_common[n].caveat || "") }])),
    models,
    agreement: { verdict: String(a.verdict || "single"), answered: a.answered || null, geminiTop: digitsOf(a.gemini_top) || null, opusTop: digitsOf(a.opus_top) || null, shared: codes(a.shared), onlyGemini: codes(a.only_gemini), onlyOpus: codes(a.only_opus) },
    dropped: Array.isArray(json.dropped) ? json.dropped : [],
    cache: json.cache || null,
    requestHash: typeof json.request_hash === "string" ? json.request_hash : (json.cache && json.cache.key) || null,
    final: false,
  };
}

/**
 * 지도 요청. `questionKeys`는 현재 트리 질문(키·문구·옵션값·라벨) — 서버가 LLM 가지를 이 키에만 매핑한다.
 * 종량제 두 모델을 부르므로 호출자는 트리가 후보를 못 냈을 때만 자동으로, 그 밖에는 버튼으로 부른다(서버 캐시 24시간).
 */
export async function fetchBranchMap({ query, facts, productDetails, liveCodes = [], liveNames = {}, questionKeys = [], signal, fetchImpl = fetch }) {
  try {
    const body = {
      q: query, facts: facts || {}, product_details: productDetails || null,
      live_codes: (liveCodes || []).map((c) => ({ code: c, name: liveNames[c] || undefined })),
      question_keys: (questionKeys || []).map((k) => ({ key: k.key, prompt: k.prompt || "", values: k.values || [], labels: k.labels || {} })),
    };
    const r = await fetchImpl(MAP_PATH, { method: "POST", headers: authHeaders(), signal, body: JSON.stringify(body) });
    if (!r || !r.ok) return { available: false, status: r ? r.status : 0 };
    const json = await r.json();
    const map = normalizeMap(json);
    return map ? { available: true, map } : { available: false, status: 200, reason: "malformed" };
  } catch (e) {
    if (e && e.name === "AbortError") return { available: false, aborted: true };
    return { available: false, error: String(e?.message || e) };
  }
}

/** 사용자가 LLM 지도의 제안(가지 선택지·물품 설명)을 채택했을 때 어느 모델의 것이었는지 남긴다 — 이중 모델 실험의 채택 지표. 실패해도 조용하다. */
export async function recordMapAdoption(requestHash, models, fetchImpl = fetch) {
  const set = modelsOf(models);
  if (!requestHash || !set.length) return false;
  const signal = set.length > 1 ? "map_both" : set[0] === "opus" ? "map_opus" : "map_gemini";
  try {
    const r = await fetchImpl("/api/onto/proposal/adoption", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ request_hash: requestHash, signal }) });
    return Boolean(r && r.ok);
  } catch (_) { return false; }
}

export function normalizeAsk(json) {
  if (!json || typeof json !== "object" || typeof json.answer !== "string") return null;
  return {
    answer: json.answer.slice(0, 1200),
    model: typeof json.model === "string" ? json.model : "",
    modelKey: json.model_key === "opus" ? "opus" : "gemini",
    attachmentsUsed: Number(json.attachments_used) || 0,
    inTokens: Number(json.in_tokens) || 0,
    outTokens: Number(json.out_tokens) || 0,
    citedCodes: (Array.isArray(json.cited_codes) ? json.cited_codes : []).map(digitsOf).filter((d) => d.length >= 4),
    proposedFacts: (Array.isArray(json.proposed_facts) ? json.proposed_facts : []).filter((f) => f && typeof f.fact_key === "string" && f.value != null).map((f) => ({ key: f.fact_key, value: String(f.value), label: f.label ? String(f.label) : null })),
    basis: typeof json.basis === "string" ? json.basis : "",
    suggestions: (Array.isArray(json.suggestions) ? json.suggestions : []).map(String).slice(0, 3),
  };
}

export async function askBranch({ query, question, facts, liveCodes, liveNames = {}, hs4s, questionKeys = [], history = [], model = "gemini", attachments = [], signal, fetchImpl = fetch }) {
  try {
    const body = {
      q: query, question, facts: facts || {}, model: model === "opus" ? "opus" : "gemini",
      ...(attachments && attachments.length ? { attachments } : {}),
      live_codes: (liveCodes || []).map((c) => ({ code: c, name: liveNames[c] || undefined })),
      hs4: hs4s || [],
      question_keys: (questionKeys || []).map((k) => ({ key: k.key, values: k.values || [] })),
      history: (history || []).slice(-6).map((h) => ({ question: String(h.question || ""), answer: String(h.answer || "") })),
    };
    const r = await fetchImpl(ASK_PATH, { method: "POST", headers: authHeaders(), signal, body: JSON.stringify(body) });
    if (!r || !r.ok) return { available: false, status: r ? r.status : 0 };
    const ask = normalizeAsk(await r.json());
    return ask ? { available: true, ask } : { available: false, status: 200, reason: "malformed" };
  } catch (e) {
    if (e && e.name === "AbortError") return { available: false, aborted: true };
    return { available: false, error: String(e?.message || e) };
  }
}

/** LLM 호출 집계(이번 달·오늘, 모델별). 인증 필요. 표가 없거나 실패면 null(fail-open). */
export function normalizeUsage(json) {
  if (!json || typeof json !== "object") return null;
  const period = (p) => {
    const models = {};
    Object.entries((p && p.models) || {}).forEach(([k, v]) => { models[k] = { calls: Number(v?.calls) || 0, inTokens: Number(v?.in_tokens) || 0, outTokens: Number(v?.out_tokens) || 0, credits: Number(v?.credits) || 0, costKrw: v && v.cost_krw != null ? Number(v.cost_krw) : null }; });
    return { calls: Number(p?.calls) || 0, credits: Number(p?.credits) || 0, costKrw: p && p.cost_krw != null ? Number(p.cost_krw) : null, models };
  };
  return {
    available: json.available !== false, reason: json.reason || null, asOf: json.as_of || null,
    // 2026-09-15 크레딧: scope user(내 것, 원가 없음) | all(관리자, 원가·예산 포함). 가중치는 서버 값 그대로.
    scope: json.scope === "all" ? "all" : "user",
    creditWeights: json.credit_weights && typeof json.credit_weights === "object" ? { basic: Number(json.credit_weights.basic) || 0, precise: Number(json.credit_weights.precise) || 0, attachment: Number(json.credit_weights.attachment) || 0 } : null,
    month: period(json.month), today: period(json.today),
    priced: Boolean(json.prices && json.prices.configured),
    priceNote: json.prices && typeof json.prices.note === "string" ? json.prices.note : "",
    budget: json.budget && Number(json.budget.monthly_krw) > 0 ? { monthlyKrw: Number(json.budget.monthly_krw), usedKrw: json.budget.used_krw != null ? Number(json.budget.used_krw) : null, remainingKrw: json.budget.remaining_krw != null ? Number(json.budget.remaining_krw) : null, over: Boolean(json.budget.over) } : null,
    history: (Array.isArray(json.history) ? json.history : []).map((r) => ({ at: String(r.at || ""), model: String(r.model || ""), modelName: String(r.model_name || ""), kind: String(r.kind || "other"), status: String(r.status || ""), inTokens: Number(r.in_tokens) || 0, outTokens: Number(r.out_tokens) || 0, credits: r.credits != null ? Number(r.credits) : null, costKrw: r.cost_krw != null ? Number(r.cost_krw) : null, settledKrw: r.settled_krw != null ? Number(r.settled_krw) : null })).filter((r) => r.at),
    prices: json.prices && typeof json.prices === "object" ? { geminiIn: Number(json.prices.gemini_in_krw_per_1k) || 0, geminiOut: Number(json.prices.gemini_out_krw_per_1k) || 0, opusIn: Number(json.prices.opus_in_krw_per_1k) || 0, opusOut: Number(json.prices.opus_out_krw_per_1k) || 0 } : null,
  };
}

export async function fetchUsage({ days = 0, signal, fetchImpl = fetch } = {}) {
  try {
    const r = await fetchImpl(days > 0 ? `${USAGE_PATH}?days=${days}` : USAGE_PATH, { method: "GET", headers: authHeaders(), signal });
    if (!r || !r.ok) return null;
    return normalizeUsage(await r.json());
  } catch (_) { return null; }
}

/** 백엔드 없이도 쓰는 상태별 질문 제안(설계 §3-2, 1차 규칙 기반). */
export function defaultSuggestions({ liveCodes = [], answeredCount = 0, narrowed = null }) {
  if (narrowed) return [`${narrowed.slice(0, 4)}.${narrowed.slice(4, 6)}-${narrowed.slice(6)}으로 좁혀졌는데 확정 전에 무엇을 더 봐야 하나요?`, "세율과 요건은 어디서 확인하나요?", "비슷한 결정례가 있나요?"];
  if (liveCodes.length === 1) return [`왜 ${liveCodes[0].slice(0, 4)}.${liveCodes[0].slice(4, 6)}만 남았나요?`, "국내 세번은 무엇으로 가르나요?", "이 세번의 결정례가 있나요?"];
  if (answeredCount === 0) return ["이 후보들은 무엇으로 갈리나요?", "가장 흔한 결론은 무엇이고 왜인가요?", "세율이 후보마다 다른가요?"];
  return ["남은 후보를 가르는 사실은 무엇인가요?", "이 답으로 어떤 후보가 떨어졌나요?", "확정 전에 확인할 것은 무엇인가요?"];
}
